// Contact resolution + call-state presentation helpers for the AgencyCallModal (STSMPD-1322).

import { AgencyCallContactOption, AgencyAccount, AgencyProfile } from './AgencyCallModal.types'
import { CallRequestView, CallStatus, TERMINAL_CALL_STATUSES } from 'syft-acp-core/store/agency-call/types'

/**
 * Builds the de-duplicated list of callable contacts for an allocation, in priority order:
 *   1. contacts pre-resolved on the row (if the caller supplied any),
 *   2. the agency head-office number (GET /admin/agencies/:id),
 *   3. each branch/account number (POST /admin/agencies/:id/accounts/search).
 * Phone numbers are trimmed and de-duplicated; entries without a number are dropped.
 */
export const resolveContacts = ({
  preResolved,
  agencyProfile,
  agencyAccounts,
  agencyName,
}: {
  preResolved?: AgencyCallContactOption[]
  agencyProfile?: AgencyProfile
  agencyAccounts?: AgencyAccount[]
  agencyName?: string
}): AgencyCallContactOption[] => {
  const candidates: AgencyCallContactOption[] = []

  preResolved?.forEach(c => c?.phone && candidates.push({ phone: c.phone, label: c.label }))

  if (agencyProfile?.telephone_number) {
    candidates.push({
      phone: agencyProfile.telephone_number,
      label: `${agencyProfile.name || agencyName || 'Agency'} (head office)`,
    })
  }

  agencyAccounts?.forEach(account => {
    if (account?.telephone_number) {
      const branch = account.branch_name || account.name || `Account ${account.id}`
      candidates.push({ phone: account.telephone_number, label: branch })
    }
  })

  const seen = new Set<string>()
  return candidates.reduce<AgencyCallContactOption[]>((acc, contact) => {
    const phone = contact.phone.trim()
    if (!phone || seen.has(phone)) return acc
    seen.add(phone)
    acc.push({ phone, label: contact.label })
    return acc
  }, [])
}

/**
 * Resolve the canonical agency name to send to the call service.
 *
 * Priority:
 *   1. the fetched agency profile name (GET /admin/agencies/:id) — the clean,
 *      canonical name;
 *   2. a normalized fallback from the row's `agency_name`. NB the launch site sets
 *      that from `subRowData.allocation_name`, which `filterAllocationName()`
 *      documents as a comma-separated display string (e.g. "Corus, Daventry"), so
 *      we take only the first segment rather than send the raw multi-value string.
 *
 * Returns `undefined` when nothing usable is available, so the service falls back
 * to its own resolver (GET /admin/agencies/:id → contact-label heuristic) instead
 * of treating a raw ACP display string as the canonical name.
 */
export const resolveAgencyName = (
  rowAgencyName?: string,
  agencyProfile?: AgencyProfile,
): string | undefined => {
  const profileName = agencyProfile?.name?.trim()
  if (profileName) return profileName
  // `allocation_name` is comma-joined display values — use the first segment only.
  const firstSegment = rowAgencyName?.split(',')[0]?.trim()
  return firstSegment || undefined
}

/** Very light E.164-ish sanity check for the free-type fallback. */
export const isLikelyValidPhone = (phone: string): boolean => /^\+?[0-9\s()-]{7,}$/.test(phone.trim())

export const isTerminalStatus = (status?: CallStatus | null): boolean =>
  status != null && TERMINAL_CALL_STATUSES.includes(status)

/** Human-readable line describing the current call state for the modal body. */
export const describeCallState = (view?: CallRequestView | null): string => {
  if (!view) return ''
  if (view.last_state) return view.last_state
  switch (view.status) {
    case 'queued':
      return 'Queued'
    case 'dialing':
      return 'On call'
    case 'answered':
      return 'Answered'
    case 'no_answer':
      return 'No answer'
    case 'voicemail':
      return 'Voicemail'
    case 'failed':
      return 'Failed'
    case 'completed':
      return 'Outcome'
    default:
      return ''
  }
}

const OUTCOME_LABELS: Record<string, string> = {
  can_fill: 'Can fill',
  partial: 'Partial fill',
  cannot_fill: 'Cannot fill',
  callback: 'Callback requested',
  no_answer: 'No answer',
  voicemail: 'Voicemail',
  failed: 'Failed',
}

export const outcomeLabel = (status?: string): string =>
  (status && OUTCOME_LABELS[status]) || status || 'Unknown'

/** Toast level for a finished call, used by notify(). */
export const toastLevelForStatus = (status: CallStatus): 'success' | 'warning' | 'error' => {
  if (status === 'completed') return 'success'
  if (status === 'failed') return 'error'
  return 'warning'
}
